import React, { useCallback, useEffect, useState } from 'react'
import { useAppSelector } from '../../../store/hooks'
import { selectAccountId } from '../../../store/auth/authSlice'
import { SelectDropdown, SelectOption } from '../../../components/SelectDropdown/SelectDropdown'
import { useGetIntegrationTeammatesQuery } from '../../../store/integrations/integrations'
import { User } from '../../../models/User'
import { getUserDisplayName } from '../../../apps/professional-app/fa/common/aifeatures/utils'
import { getContactDropdownComponent } from '../ContactDropdownItems'
import { IntegrationServiceId } from '../../../common/integrations'

export const UserSelectDropdown: React.FC<{
  serviceId: string
  onOptionSelected: (optionId: string | null) => void
}> = ({ serviceId, onOptionSelected }) => {
  const accountId = useAppSelector(selectAccountId)
  const [searchedTeamOptions, setSearchedTeamOptions] = useState<SelectOption[] | null>(null)

  const { data: users, isFetching: isUsersFetching } = useGetIntegrationTeammatesQuery({
    accountId,
    connector: serviceId,
  })

  const getTeamOptions = useCallback(
    (users: User[]) => {
      return users.map((user) => {
        const userIntegration = user.integrations?.find((i) => i.serviceId === serviceId)
        const userLabel = getUserDisplayName(user)
        return { id: userIntegration!.externalId, label: userLabel }
      })
    },
    [serviceId],
  )

  useEffect(() => {
    if (users?.data) {
      setSearchedTeamOptions(getTeamOptions(users.data))
    }
  }, [users, getTeamOptions])

  const onUserSearchInputChange = async (searchInput: string) => {
    if (!users?.data) {
      return
    }

    if (!searchInput) {
      setSearchedTeamOptions(getTeamOptions(users.data))
      return
    }

    const filteredUsers = users.data.filter((user) => user.name?.toLowerCase().includes(searchInput.toLowerCase()))

    setSearchedTeamOptions(getTeamOptions(filteredUsers))
  }

  return (
    <SelectDropdown
      initialSelectedOption={null}
      options={searchedTeamOptions ?? []}
      className='py-2 zocks-px-0.75'
      onOptionSelected={(option) => onOptionSelected(option ? option.id : null)}
      onDeselect={() => onOptionSelected(null)}
      DropDownItemOptionComponent={getContactDropdownComponent(serviceId as IntegrationServiceId)}
      searchable={true}
      loading={isUsersFetching}
      onSearchInputChange={onUserSearchInputChange}
      placeholder='Select user'
      showDeselectOption={true}
      deselectOptionLabel={'Deselect user'}
      forceDefaultDeselectOptionComponent={true}
    />
  )
}
